import type { ComponentProps } from 'react';
import { NavLink, useLocation } from 'react-router';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSession } from '@/auth';
import { navItemsFor, type NavItem } from '@/routes/roleRoutes';
import { messages } from '@/i18n';
import { IS_MAC, useSidebarCollapsed } from './useSidebarCollapsed';

const SHORTCUT_LABEL = IS_MAC ? '⌘B' : 'Ctrl+B';

/**
 * Renders only the current role's own navigation. There is no filtering of a
 * global list here, and there should not be: a single source per role is what
 * makes "no navigation item outside that role's set" a testable claim.
 *
 * Every role renders this, from one item up — it adapts to viewport width via
 * CSS (hidden below `md`), not to who is signed in. Below `md`, MobileNav's
 * drawer is the same list.
 *
 * On desktop it collapses to an icon rail rather than disappearing, so every
 * destination stays one click away. Labels stay in the DOM while collapsed
 * (clipped, not removed), so links keep their accessible names; a tooltip
 * shows the label to sighted users instead.
 */
export function SideNav() {
  const session = useSession();
  const items = navItemsFor(session.role);
  const location = useLocation();
  const { collapsed, toggle } = useSidebarCollapsed();
  const toggleLabel = collapsed ? messages.common.expandSidebar : messages.common.collapseSidebar;

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={300}>
      <aside
        data-collapsed={collapsed}
        className={cn(
          'border-border/20 hidden shrink-0 overflow-hidden md:sticky md:top-[57px] md:flex md:h-[calc(100dvh-57px)] md:flex-col md:border-r',
          'transition-[width] duration-200 ease-out motion-reduce:transition-none',
          collapsed ? 'md:w-16' : 'md:w-64',
        )}
      >
        <nav
          id="main-nav"
          aria-label="Main"
          className="flex flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto p-3"
        >
          {items.map(({ to, label, icon }) => (
            // Expanded, the label is already visible — the tooltip stays shut.
            <Tooltip key={to} {...(collapsed ? {} : { open: false })}>
              <TooltipTrigger asChild>
                <SideNavLink
                  to={{ pathname: to, search: location.search }}
                  label={label}
                  icon={icon}
                  collapsed={collapsed}
                />
              </TooltipTrigger>
              <TooltipContent side="right">{label}</TooltipContent>
            </Tooltip>
          ))}
        </nav>

        <div className="border-border/20 border-t p-3">
          <Tooltip {...(collapsed ? {} : { open: false })}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggle}
                aria-label={toggleLabel}
                aria-expanded={!collapsed}
                aria-controls="main-nav"
                aria-keyshortcuts={IS_MAC ? 'Meta+B' : 'Control+B'}
                className="text-muted-foreground hover:bg-secondary/70 hover:text-foreground flex w-full items-center gap-3 overflow-hidden rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors"
              >
                {collapsed ? (
                  <PanelLeftOpen className="size-4 shrink-0" aria-hidden />
                ) : (
                  <PanelLeftClose className="size-4 shrink-0" aria-hidden />
                )}
                <span
                  className={cn(
                    'flex flex-1 items-center justify-between gap-2 transition-opacity duration-150 motion-reduce:transition-none',
                    collapsed && 'opacity-0',
                  )}
                  aria-hidden
                >
                  {messages.common.collapseSidebar}
                  <kbd className="border-border/40 text-muted-foreground rounded border px-1.5 font-sans text-[10px]">
                    {SHORTCUT_LABEL}
                  </kbd>
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {toggleLabel} <span className="text-muted-foreground ml-1">{SHORTCUT_LABEL}</span>
            </TooltipContent>
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  );
}

type SideNavLinkProps = Omit<ComponentProps<typeof NavLink>, 'className' | 'children'> &
  Pick<NavItem, 'label' | 'icon'> & { collapsed: boolean };

/**
 * A component of its own so TooltipTrigger's Slot merges its props into this,
 * not into NavLink directly: Slot concatenates `className` as a string, which
 * would stringify NavLink's `({ isActive }) => …` function and drop the
 * active styling.
 */
function SideNavLink({ label, icon: Icon, collapsed, ...props }: SideNavLinkProps) {
  return (
    <NavLink
      {...props}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 overflow-hidden rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground font-medium'
            : 'text-foreground/80 hover:bg-secondary/70',
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      <span
        className={cn(
          'truncate transition-opacity duration-150 motion-reduce:transition-none',
          collapsed && 'opacity-0',
        )}
      >
        {label}
      </span>
    </NavLink>
  );
}
